/**
 * Runtime-config hot reload.
 *
 * Today `runtime-config.json` is fetched once at boot. To flip a
 * feature flag without a SPA redeploy, the operator must bounce
 * the pod. This module adds a periodic re-fetch in non-`local`
 * environments:
 *
 *   - Every 5 minutes, GET `/config/runtime-config.json` (no-store).
 *   - Validate the new payload against the existing one.
 *   - If valid and different: dispatch a CustomEvent so the UI can
 *     surface a toast "Configuration updated — refresh to apply".
 *   - If invalid: keep the old config, log to telemetry.
 *
 * The reload is best-effort. The SWR-cached runtime-config from the
 * service-worker (P0 §1.6) means even a hard reload picks up the
 * new config within the cache TTL.
 */

import {
  getOptionalRuntimeConfig,
  RUNTIME_CONFIG_UPDATED_EVENT,
  setRuntimeConfig,
  validateRuntimeConfig,
  type RuntimeConfig,
} from './runtime-config';
import { setMockAuthDemoMode } from '../auth/mock-auth-loader';
import { reportError, reportMetric } from '../observability/telemetry';

const POLL_MS = 5 * 60_000; // 5 minutes
const RUNTIME_CONFIG_URL = '/config/runtime-config.json';

declare const __APP_VERSION__: string;

/** Re-exported for backward compatibility — see
 *  `runtime-config.ts` for the canonical definition. */
export { RUNTIME_CONFIG_UPDATED_EVENT };

type Listener = (cfg: RuntimeConfig) => void;
const listeners = new Set<Listener>();

let timer: number | null = null;

/** Subscribe to runtime-config updates. Returns an unsubscribe fn. */
export function onRuntimeConfigChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Stable JSON for diff comparison (sorted keys). */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
}

/**
 * Detect whether the given runtime config looks like a local demo
 * deployment. We treat `authMode=mock` AND a localhost-shaped
 * `apiBaseUrl` as "demo mode" — this is the case for the docker
 * demo container that ships with the repo, and the only place
 * where `authMode=mock` is allowed in a production build.
 *
 * Real production tenants always use `authMode=zitadel` with a
 * real ZITADEL issuer, so they never trigger this branch.
 */
function isLocalDemoDeployment(cfg: RuntimeConfig): boolean {
  if (cfg.authMode !== 'mock') return false;
  try {
    const api = new URL(cfg.apiBaseUrl);
    return api.hostname === 'localhost' || api.hostname === '127.0.0.1';
  } catch {
    return false;
  }
}

async function fetchAndDiff(): Promise<void> {
  try {
    const res = await fetch(RUNTIME_CONFIG_URL, { cache: 'no-store' });
    if (!res.ok) {
      reportMetric('config.reload.http_error', 1, { status: res.status });
      return;
    }
    const raw = (await res.json()) as RuntimeConfig;
    const oldCfg = getOptionalRuntimeConfig();
    try {
      validateRuntimeConfig(raw);
    } catch (err) {
      reportError(err, { feature: 'config-reload', op: 'validate' });
      reportMetric('config.reload.invalid', 1);
      return;
    }
    if (oldCfg && stableStringify(oldCfg) === stableStringify(raw)) {
      reportMetric('config.reload.unchanged', 1);
      return;
    }
    setRuntimeConfig(raw);
    // Re-evaluate the demo-mode flag every time the config reloads
    // so a tenant can flip from mock to zitadel by editing
    // runtime-config.json without a redeploy.
    setMockAuthDemoMode(isLocalDemoDeployment(raw));
    reportMetric('config.reload.updated', 1);
    listeners.forEach((l) => {
      try {
        l(raw);
      } catch (err) {
        reportError(err, { feature: 'config-reload', op: 'listener' });
      }
    });
    window.dispatchEvent(
      new CustomEvent(RUNTIME_CONFIG_UPDATED_EVENT, {
        detail: { cfg: raw, ts: Date.now() },
      }),
    );
  } catch (err) {
    reportError(err, { feature: 'config-reload', op: 'fetch' });
  }
}

/** Start the periodic poll. Idempotent — multiple calls don't
 *  spawn duplicate timers. */
export function startRuntimeConfigPolling(intervalMs: number = POLL_MS): void {
  if (timer !== null) return;
  if (typeof window === 'undefined') return;
  if (!import.meta.env.PROD) return; // dev: no polling, would surprise HMR
  // Defer the first poll by a few seconds so it doesn't race with
  // the boot-time loadRuntimeConfig().
  timer = window.setTimeout(() => {
    void fetchAndDiff();
    timer = window.setInterval(() => void fetchAndDiff(), intervalMs);
  }, 10_000);
}

/** Stop the periodic poll (used by tests). */
export function stopRuntimeConfigPolling(): void {
  if (timer !== null) {
    window.clearTimeout(timer);
    timer = null;
  }
}

/** Test helper: drop the listener set + stop any active timer. */
export function __resetForTests(): void {
  listeners.clear();
  stopRuntimeConfigPolling();
}

/** Build a copy-paste-friendly JSON dump for support tickets. */
export function buildSupportInfo(): string {
  const cfg = getOptionalRuntimeConfig();
  return JSON.stringify(
    {
      ts: new Date().toISOString(),
      version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev',
      url: window.location.href,
      config: cfg
        ? {
            env: cfg.env,
            authMode: cfg.authMode,
            clientId: cfg.client.id,
            // defaultSiteId was removed in the convergence target
            // (plan §6.4); SiteContext owns the active site selection.
            featureFlags: cfg.featureFlags,
          }
        : null,
      performance: {
        navStart: performance.timeOrigin,
        memory: (performance as unknown as { memory?: { usedJSHeapSize: number } })
          .memory?.usedJSHeapSize,
      },
    },
    null,
    2,
  );
}
